import React, { useEffect, useMemo, useState } from 'react';
import { Form, Input, Select, Space, Typography, Divider, App, Alert, Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../../lib/api';
import { FileUploader } from '../file/FileUploader';
import { ACCEPTED_DOCUMENT_LABEL, ACCEPTED_DOCUMENT_TYPES } from '../../constants/documentAccept';
import { RichTextEditor } from '../common/RichTextEditor';
import { withDevModalFilePath } from '../common';

const { Text } = Typography;
const { Modal } = withDevModalFilePath('client/src/components/document/DocumentCreateModal.tsx');

const ROLE_PRIORITY: Record<string, number> = {
  viewer: 0,
  user: 1,
  manager: 2,
  admin: 3,
};

type DocumentAssociationEntityType = 'location' | 'equipment' | 'part' | 'supplier' | 'user';
type DocumentRelatedEntityType = DocumentAssociationEntityType | 'location_building' | 'purchase_order';
type DocumentTypeOption = {
  id: string;
  name: string;
  category: 'general' | 'official';
  isEnabled?: boolean;
  relatedEntityType?: DocumentRelatedEntityType | null;
  readRole?: 'viewer' | 'user' | 'manager' | 'admin' | null;
  writeRole?: 'viewer' | 'user' | 'manager' | 'admin' | null;
};

type UploadedDocumentFile = {
  id?: string;
  name?: string;
  originalName?: string;
  fileName?: string;
};

interface DocumentCreateModalProps {
  open: boolean;
  entityType: DocumentAssociationEntityType;
  entityId: string;
  allowOfficial?: boolean;
  allowBuilding?: boolean;
  restrictToEntityType?: boolean;
  defaultTitle?: string;
  onCancel: () => void;
  onSuccess?: () => void;
}

export const DocumentCreateModal: React.FC<DocumentCreateModalProps> = ({
  open,
  entityType,
  entityId,
  allowOfficial = false,
  allowBuilding = false,
  restrictToEntityType = false,
  defaultTitle,
  onCancel,
  onSuccess,
}) => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const utils = trpc.useUtils();
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ id: string; name: string }>>([]);
  const { data: profile } = trpc.user.getProfile.useQuery();

  const { data: documentTypes = [], isLoading: isTypesLoading } = trpc.document.listTypes.useQuery();
  const typedDocumentTypes = documentTypes as DocumentTypeOption[];

  const hasMinimumRole = React.useCallback((role: string, minimum: string) =>
    (ROLE_PRIORITY[role] ?? 0) >= (ROLE_PRIORITY[minimum] ?? 0), []);
  const actorRole = profile?.role ?? 'viewer';

  const isRelatedTypeCompatible = React.useCallback((relatedType?: DocumentRelatedEntityType | null) => {
    if (!relatedType) return true;
    if (relatedType === 'location_building') {
      return entityType === 'location' && allowBuilding;
    }
    return relatedType === entityType;
  }, [allowBuilding, entityType]);

  const enabledTypes = useMemo(() => {
    return typedDocumentTypes.filter((type) => {
      if (!type.isEnabled) return false;
      if (!hasMinimumRole(actorRole, type.writeRole ?? 'user')) return false;
      if (restrictToEntityType) {
        return isRelatedTypeCompatible(type.relatedEntityType);
      }
      if (type.relatedEntityType && !isRelatedTypeCompatible(type.relatedEntityType)) {
        return false;
      }
      return true;
    });
  }, [typedDocumentTypes, restrictToEntityType, actorRole, hasMinimumRole, isRelatedTypeCompatible]);

  const selectableTypes = useMemo(
    () => enabledTypes.filter((type) => !(type.category === 'official' && !allowOfficial)),
    [allowOfficial, enabledTypes]
  );

  const createMutation = trpc.document.create.useMutation({
    onSuccess: async () => {
      message.success('書類を登録しました');
      setUploadedFiles([]);
      form.resetFields();
      await utils.document.list.invalidate();
      onSuccess?.();
    },
    onError: (error) => {
      message.error(error.message || '書類登録に失敗しました');
    },
  });

  useEffect(() => {
    if (!open) {
      return;
    }
    form.resetFields();
    setUploadedFiles([]);
    if (defaultTitle) {
      form.setFieldsValue({ title: defaultTitle });
    }
  }, [open, defaultTitle, form]);

  const applyTitleFromUpload = (file: UploadedDocumentFile | null | undefined) => {
    const currentTitle = form.getFieldValue('title');
    if (currentTitle) return;
    const inferredTitle = file?.originalName ?? file?.fileName ?? file?.name;
    if (inferredTitle) {
      const dotIndex = inferredTitle.lastIndexOf('.');
      const title = dotIndex > 0 ? inferredTitle.slice(0, dotIndex) : inferredTitle;
      form.setFieldsValue({ title });
    }
  };

  const normalizeDocumentFileName = (fileName?: string | null) => {
    const safeName = (fileName ?? '').trim();
    if (!safeName) return '';
    return safeName.toLowerCase();
  };

  const handleOk = async () => {
    const values = await form.validateFields();
    if (uploadedFiles.length === 0) {
      message.warning('ファイルをアップロードしてください');
      return;
    }

    createMutation.mutate({
      title: values.title,
      description: values.description ?? undefined,
      documentTypeId: values.documentTypeId,
      association: {
        entityType,
        entityId,
      },
      files: uploadedFiles.map((file) => ({ fileId: file.id })),
    });
  };

  const hasTypes = selectableTypes.length > 0;

  return (
    <Modal
      title="書類登録"
      open={open}
      onCancel={onCancel}
      onOk={() => void handleOk()}
      okText="登録"
      cancelText="キャンセル"
      confirmLoading={createMutation.isPending}
      okButtonProps={{ disabled: !hasTypes }}
      destroyOnHidden
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {!hasTypes && !isTypesLoading && (
          <Alert
            type="info"
            message="利用可能な書類カテゴリがありません"
            description={(
              <Space direction="vertical" size={4}>
                <Text>対応カテゴリを追加してください。</Text>
                <Button type="link" onClick={() => navigate('/app/categories#document')} style={{ padding: 0 }}>
                  書類カテゴリを追加する
                </Button>
              </Space>
            )}
            showIcon
          />
        )}
        <Text type="secondary">アップロード済み: {uploadedFiles.length}件</Text>
        <FileUploader
          entityType={entityType}
          entityId={entityId}
          category="document"
          accept={ACCEPTED_DOCUMENT_TYPES}
          acceptLabel={ACCEPTED_DOCUMENT_LABEL}
          maxFiles={10}
          maxSize={25}
          disallowDuplicateNames
          existingFileNames={uploadedFiles.map((file) => file.name)}
          normalizeDuplicateName={normalizeDocumentFileName}
          onUploadSuccess={(file) => {
            if (file?.id) {
              const name = (file?.originalName ?? file?.fileName ?? file?.name ?? '').toString();
              setUploadedFiles((prev) => {
                const normalized = normalizeDocumentFileName(name);
                const exists = prev.some((item) => normalizeDocumentFileName(item.name) === normalized);
                if (exists) {
                  message.error('同名ファイルはアップロードできません');
                  return prev;
                }
                return [...prev, { id: file.id, name }];
              });
            }
            applyTitleFromUpload(file);
          }}
        />
        <Divider style={{ margin: '8px 0' }} />
        <Form form={form} layout="vertical">
          <Form.Item
            name="title"
            label="書類名"
            rules={[{ required: true, message: '書類名を入力してください' }]}
          >
            <Input placeholder="例: 点検報告書 2025" maxLength={255} />
          </Form.Item>
          <Form.Item
            name="description"
            label="説明"
            valuePropName="value"
            trigger="onChange"
          >
            <RichTextEditor placeholder="書類の詳細や補足を記載" minHeight={160} />
          </Form.Item>
          <Form.Item
            name="documentTypeId"
            label="書類カテゴリ"
            rules={[{ required: true, message: '書類カテゴリを選択してください' }]}
          >
            <Select
              placeholder={isTypesLoading ? '読み込み中...' : '書類カテゴリを選択'}
              loading={isTypesLoading}
              options={enabledTypes.map((type) => ({
                label: type.name,
                value: type.id,
                disabled: type.category === 'official' && !allowOfficial,
              }))}
            />
          </Form.Item>
        </Form>
      </Space>
    </Modal>
  );
};

export default DocumentCreateModal;
